import {ID} from '@wandb/weave/core';
import {getType} from 'typesafe-actions';

import {setInShallowClone} from '../../util/utility';
import * as ViewActions from './actions';
import * as ActionsInternal from './actionsInternal';
import * as CustomRunNamesActions from './customRunNames/actions';
import * as FilterActions from './filter/actions';
import * as GroupSelectionsActions from './groupSelections/actions';
import * as GroupSelectionsUtils from './groupSelections/utils';
import {handleUpdateViewSpec} from './handleUpdateViewSpec';
import {markSectionPanelsAutoImmutable} from './markSectionPanelsAuto';
import * as Normalize from './normalize';
import * as PanelActions from './panel/actions';
import * as PanelBankConfigActions from './panelBankConfig/actions';
import * as PanelBankConfigActionsInternal from './panelBankConfig/actionsInternal';
import * as ClearPanelsUtils from './panelBankConfig/clearPanelsUtils';
import {movePanelAction} from './panelBankConfig/movePanelAction';
import {moveSectionAction} from './panelBankConfig/moveSectionAction';
import {undoMovePanelAction} from './panelBankConfig/undoMovePanelAction';
import * as PanelBankConfigUtils from './panelBankConfig/utils';
import * as PanelBankSectionConfigActions from './panelBankSectionConfig/actions';
import * as PanelBankSectionConfigUtils from './panelBankSectionConfig/utils';
import {type ActionType, type ViewReducerState} from './reducerSupport';
import * as RunSetActions from './runSet/actions';
import type {RunSetPart} from './runSet/types';
import {
  canPinRun,
  getGroupSelections,
  getRunSet,
  isRunPinned,
  setRunPinned,
  setRunPinnedAndBaseline,
  updateRunSet,
} from './runSet/utils';
import * as SectionActions from './section/actions';
import * as SectionUtils from './section/utils';
import * as TempSelectionsActions from './tempSelections/actions';
import {
  selectAll,
  selectAllVisible,
  selectNone,
  selectSome,
  selectToggle,
  updateTempSelectionsWithUndo,
} from './tempSelections/utils';
import * as WorkspaceSettingsActions from './workspaceSettings/actions';
import * as WorkspaceSettingsUtils from './workspaceSettings/utils';

const NO_OP_UNDO = ViewActions.noop();

function notUndoable(state: ViewReducerState): [ViewReducerState, ActionType] {
  return [state, NO_OP_UNDO];
}

const applyAndMakeInverseActionImmutableInner = (
  state: ViewReducerState,
  action: ActionType
): [ViewReducerState, ActionType] => {
  switch (action.type) {
    case getType(ActionsInternal.undoableUpdateViewSpec): {
      const {ref, prevSpec, newSpec} = action.payload;
      const newState = handleUpdateViewSpec(state, ref.id, newSpec);
      const inverseAction = ActionsInternal.undoableUpdateViewSpec(ref, {
        prevSpec: newSpec, //  Note how the specs are swapped for undo action
        newSpec: prevSpec,
      });
      return [newState, inverseAction];
    }

    case getType(CustomRunNamesActions.setCustomRunName): {
      const {ref, id, name} = action.payload;
      const newState = setInShallowClone(
        state,
        ['parts', ref.type, ref.id, id],
        name || undefined // || undefined ensures we don't keep unnecessary empty string entries
      );
      const inverseAction = CustomRunNamesActions.setCustomRunName(
        ref,
        id,
        state.parts[ref.type][ref.id][id]
      );
      return [newState, inverseAction];
    }

    case getType(GroupSelectionsActions.toggleSelection): {
      const {ref, run, depth} = action.payload;
      return GroupSelectionsUtils.toggleSelection(state, ref, run, depth);
    }

    case getType(PanelActions.setConfig): {
      const {ref, config, key} = action.payload;
      const prevPanel = state.parts[ref.type][ref.id];
      const newPanel = Object.assign({}, prevPanel, {config, key});
      const newState = setInShallowClone(
        state,
        ['parts', ref.type, ref.id],
        newPanel
      );
      const inverseAction = PanelActions.setConfig(
        action.payload.ref,
        prevPanel.config,
        prevPanel.key
      );
      return [newState, inverseAction];
    }

    case getType(PanelActions.updateConfig): {
      const {ref, configUpdate} = action.payload;
      const prevPanel = state.parts[ref.type][ref.id];
      const newPanelConfig = Object.assign({}, prevPanel.config, configUpdate);
      const isAuto = false;
      // Preserve key for panels whose identity is not tied to metric keys.
      // Clearing key is appropriate for default metric-bound panels (e.g.,
      // single-metric Run History Line Plot) so they become custom charts.
      // However, API-added panels like Weave Trace rely on a stable key
      // (e.g., 'weave-trace') for diff/dedup. Do not clear those keys.
      const preserveKeyViewTypes = new Set(['Weave Trace', 'Weave']);
      const newKey = preserveKeyViewTypes.has(prevPanel.viewType)
        ? prevPanel.key
        : undefined;
      const newPanel = Object.assign({}, prevPanel, {
        config: newPanelConfig,
        key: newKey,
        isAuto,
      });
      const newState = setInShallowClone(
        state,
        ['parts', ref.type, ref.id],
        newPanel
      );
      const inverseAction = PanelActions.setConfig(
        action.payload.ref,
        prevPanel.config,
        prevPanel.key
      );
      return [newState, inverseAction];
    }

    case getType(PanelBankConfigActions.addPanelsBySpec): {
      const {ref, specs, shouldAlphaInsert} = action.payload;
      return PanelBankConfigUtils.addPanelsBySpec(
        state,
        ref,
        specs,
        shouldAlphaInsert
      );
    }

    case getType(PanelBankConfigActions.addPanelsBySpecUndo): {
      const {ref, sectionsMetadata} = action.payload;
      return PanelBankConfigUtils.addPanelsBySpecUndo(
        state,
        ref,
        sectionsMetadata
      );
    }

    case getType(PanelBankConfigActions.clearAllPanels): {
      const {ref, workspaceSettingsRef} = action.payload;
      return ClearPanelsUtils.clearAllPanels(state, ref, workspaceSettingsRef);
    }

    case getType(PanelBankConfigActions.clearAllPanelsUndo): {
      const {ref, workspaceSettingsRef, sectionRefs} = action.payload;
      return ClearPanelsUtils.clearAllPanelsUndo(
        state,
        ref,
        workspaceSettingsRef,
        sectionRefs
      );
    }

    case getType(PanelBankConfigActions.moveSection): {
      const {ref, activeSectionRefId, targetSectionRefId} = action.payload;
      return moveSectionAction(
        state,
        ref,
        activeSectionRefId,
        targetSectionRefId
      );
    }

    case getType(PanelBankConfigActions.movePanel): {
      const {ref, active, target} = action.payload;
      return movePanelAction(state, ref, active, target);
    }

    case getType(PanelBankConfigActions.undoMovePanel): {
      const {ref, active, target} = action.payload;
      return undoMovePanelAction(state, ref, active, target);
    }

    case getType(PanelBankConfigActions.openOrCloseAllSections): {
      const {ref, isOpen} = action.payload;
      return PanelBankConfigUtils.updateSections(
        state,
        ref,
        curSectionConfig => {
          if (curSectionConfig.isOpen === isOpen) {
            return curSectionConfig;
          }
          return Object.assign({}, curSectionConfig, {isOpen});
        }
      );
    }

    case getType(PanelBankConfigActions.updateAllLinePlotSectionSettings): {
      const {ref, settings} = action.payload;
      return PanelBankConfigUtils.updateAllLinePlotSectionSettings(
        state,
        ref,
        settings
      );
    }

    case getType(PanelBankConfigActionsInternal.setAllSections): {
      const {ref, newSectionsNormalized} = action.payload;
      return PanelBankConfigUtils.updateSections(
        state,
        ref,
        (curSectionConfig, i) => newSectionsNormalized[i]
      );
    }

    case getType(PanelBankSectionConfigActions.addPanelByConfig): {
      const {ref, panel, shouldAlphaInsert, fatPanel, callbackFn} =
        action.payload;
      return PanelBankSectionConfigUtils.addPanel(
        state,
        ref,
        panel,
        shouldAlphaInsert,
        fatPanel,
        callbackFn
      );
    }

    case getType(PanelBankSectionConfigActions.addPanelByRef): {
      const {ref, panelRef, shouldAlphaInsert, fatPanel, callbackFn} =
        action.payload;
      const panel = Normalize.denormalize(state.parts, panelRef);
      return PanelBankSectionConfigUtils.addPanel(
        state,
        ref,
        panel,
        shouldAlphaInsert,
        fatPanel,
        callbackFn
      );
    }

    case getType(PanelBankSectionConfigActions.addPanelsByConfig): {
      const {ref, panels, shouldAlphaInsert} = action.payload;
      const {newState, newPanelRefs, prevIsPanelsAuto} =
        PanelBankSectionConfigUtils.addPanels(
          state,
          ref,
          panels,
          shouldAlphaInsert
        );
      const inverseAction = PanelActions.deletePanels(
        newPanelRefs,
        ref,
        undefined,
        undefined,
        {value: prevIsPanelsAuto}
      );
      return [newState, inverseAction];
    }

    case getType(PanelBankSectionConfigActions.toggleIsOpen): {
      const {ref} = action.payload;
      return PanelBankSectionConfigUtils.toggleIsOpen(state, ref);
    }

    case getType(PanelBankSectionConfigActions.updateFlowConfig): {
      const {ref, newFlowConfig, operation} = action.payload;
      const prevFlowConfig = state.parts[ref.type][ref.id].flowConfig;
      const newState = setInShallowClone(
        state,
        ['parts', ref.type, ref.id, 'flowConfig'],
        operation === 'PATCH'
          ? {...prevFlowConfig, ...(newFlowConfig ?? {})}
          : newFlowConfig
      );
      return [
        newState,
        PanelBankSectionConfigActions.updateFlowConfig(
          ref,
          prevFlowConfig,
          'PUT'
        ),
      ];
    }

    case getType(PanelBankSectionConfigActions.updateLinePlotSectionSettings): {
      const {ref, settings} = action.payload;
      return PanelBankSectionConfigUtils.updateLinePlotSectionSettings(
        state,
        ref,
        settings
      );
    }

    case getType(PanelBankSectionConfigActions.updateSectionMediaSettings): {
      const {ref, settings} = action.payload;
      return PanelBankSectionConfigUtils.updateSectionMediaSettings(
        state,
        ref,
        settings
      );
    }

    case getType(PanelBankSectionConfigActions.unsetSectionMediaSetting): {
      const {ref, field} = action.payload;
      return PanelBankSectionConfigUtils.unsetSectionMediaSetting(
        state,
        ref,
        field
      );
    }
    case getType(PanelBankSectionConfigActions.updateName): {
      const {ref, newName, panelAutoVals} = action.payload;
      return PanelBankSectionConfigUtils.updateName(
        state,
        ref,
        newName,
        panelAutoVals
      );
    }

    case getType(RunSetActions.update): {
      const {ref, runSetUpdate} = action.payload;
      const runSet = getRunSet(state, ref);
      if (!runSet) {
        return notUndoable(state);
      }
      const updater =
        typeof runSetUpdate === 'function'
          ? runSetUpdate
          : (runSet: RunSetPart) => ({...runSet, ...runSetUpdate});

      const newState = updateRunSet(state, ref, updater);

      if (newState === state) {
        // No change was made to the runset, so do nothing.
        return notUndoable(state);
      }

      const inverseAction = RunSetActions.set(ref, runSet);
      return [newState, inverseAction];
    }

    case getType(RunSetActions.setPinnedRun): {
      const {ref, run, pinned} = action.payload;
      const runSet = getRunSet(state, ref);
      if (!runSet) {
        return notUndoable(state);
      }

      if (pinned && !canPinRun(runSet)) {
        // Can't pin run for some reason (e.g. too many runs already pinned)
        return notUndoable(state);
      }

      const newState = updateRunSet(state, ref, runSet =>
        setRunPinned(runSet, run, pinned)
      );

      return [newState, RunSetActions.setPinnedRun(ref, run, !pinned)];
    }
    case getType(RunSetActions.setBaselineRun): {
      const {ref, run} = action.payload;
      const runSet = getRunSet(state, ref);
      if (!runSet) {
        return notUndoable(state);
      }

      if (run && !canPinRun(runSet) && !isRunPinned(runSet.pinnedRunIds, run)) {
        // Can't pin run for some reason (e.g. too many runs already pinned),
        // which means we can't set a baseline either.
        return notUndoable(state);
      }

      const currentBaselineRun = runSet.baselineRunId;
      const newState = updateRunSet(state, ref, runSet =>
        setRunPinnedAndBaseline(runSet, run)
      );

      return [
        newState,
        RunSetActions.setBaselineRun(
          ref,
          currentBaselineRun ? {id: currentBaselineRun} : null
        ),
      ];
    }

    case getType(WorkspaceSettingsActions.updateAutoOrganizePrefix): {
      const {workspaceSettingsRef, autoOrganizePrefix, panelBankConfigRef} =
        action.payload;
      return WorkspaceSettingsUtils.updateAutoOrganizePrefix(
        state,
        workspaceSettingsRef,
        panelBankConfigRef,
        autoOrganizePrefix
      );
    }

    case getType(WorkspaceSettingsActions.updateAutoOrganizePrefixUndo): {
      const {
        workspaceSettingsRef,
        autoOrganizePrefix,
        panelBankConfigRef,
        sectionRefs,
      } = action.payload;
      return WorkspaceSettingsUtils.updateAutoOrganizePrefixUndo(
        state,
        workspaceSettingsRef,
        panelBankConfigRef,
        sectionRefs,
        autoOrganizePrefix
      );
    }

    case getType(WorkspaceSettingsActions.updateLinePlotWorkspaceSettings): {
      const {ref, settings} = action.payload;
      return WorkspaceSettingsUtils.updateLinePlotWorkspaceSettings(
        state,
        ref,
        settings
      );
    }

    case getType(WorkspaceSettingsActions.updateWorkspaceMediaSettings): {
      const {ref, settings} = action.payload;
      return WorkspaceSettingsUtils.updateWorkspaceMediaSettings(
        state,
        ref,
        settings
      );
    }

    case getType(WorkspaceSettingsActions.unsetWorkspaceMediaSetting): {
      const {ref, key} = action.payload;
      return WorkspaceSettingsUtils.unsetWorkspaceMediaSetting(state, ref, key);
    }

    case getType(
      WorkspaceSettingsActions.updateSemanticLegendRunColorOptions
    ): {
      const {ref, semanticLegendRunColorOptions} = action.payload;
      return WorkspaceSettingsUtils.updateSemanticLegendRunColorOptions(
        state,
        ref,
        semanticLegendRunColorOptions
      );
    }

    case getType(WorkspaceSettingsActions.updateShouldAutoGeneratePanels): {
      const {workspaceSettingsRef, panelBankConfigRef, value} = action.payload;
      return WorkspaceSettingsUtils.updateShouldAutoGeneratePanels(
        state,
        workspaceSettingsRef,
        panelBankConfigRef,
        value
      );
    }

    case getType(WorkspaceSettingsActions.updateColorPaletteOption): {
      const {ref, newColorPalette} = action.payload;
      return WorkspaceSettingsUtils.updateColorPaletteOption(
        state,
        ref,
        newColorPalette
      );
    }

    case getType(WorkspaceSettingsActions.updateSemanticLegendPalette): {
      const {ref, palette} = action.payload;
      return WorkspaceSettingsUtils.updateSemanticLegendPalette(
        state,
        ref,
        palette
      );
    }
    case getType(WorkspaceSettingsActions.setWorkspaceSettings): {
      const {ref, settings} = action.payload;
      return WorkspaceSettingsUtils.setWorkspaceSettings(state, ref, settings);
    }

    case getType(WorkspaceSettingsActions.updateWorkspaceLayoutSettings): {
      const {ref, settings} = action.payload;
      return WorkspaceSettingsUtils.updateWorkspaceLayoutSettings(
        state,
        ref,
        settings
      );
    }

    case getType(PanelBankConfigActions.updateSettings): {
      const {ref, panelBankSettings} = action.payload;
      return PanelBankConfigUtils.updateSettings(state, ref, panelBankSettings);
    }

    case getType(SectionActions.addNewRunSet): {
      return SectionUtils.addNewRunSet(state, action.payload.ref);
    }

    case getType(FilterActions.set): {
      const {ref, filters} = action.payload;
      const prevFilters = state.parts.filters[ref.id];
      const newState = setInShallowClone(
        state,
        ['parts', 'filters', ref.id],
        filters
      );
      return [newState, FilterActions.set(action.payload.ref, prevFilters)];
    }

    case getType(TempSelectionsActions.selectAllVisible): {
      const {ref, runSetRef} = action.payload;

      return updateTempSelectionsWithUndo(state, ref, tempSelection =>
        selectAllVisible(
          tempSelection,
          getRunSet(state, runSetRef),
          getGroupSelections(state, runSetRef)
        )
      );
    }

    case getType(TempSelectionsActions.selectNone): {
      const {ref, runSetRef} = action.payload;

      return updateTempSelectionsWithUndo(state, ref, tempSelection =>
        selectNone(tempSelection, getGroupSelections(state, runSetRef))
      );
    }

    case getType(TempSelectionsActions.selectToggle): {
      const {ref, run, runSetRef, depth} = action.payload;

      return updateTempSelectionsWithUndo(state, ref, tempSelection =>
        selectToggle(
          tempSelection,
          getGroupSelections(state, runSetRef),
          run,
          depth
        )
      );
    }

    case getType(TempSelectionsActions.selectSome): {
      const {ref, runs, runSetRef} = action.payload;

      return updateTempSelectionsWithUndo(state, ref, tempSelection =>
        selectSome(tempSelection, getGroupSelections(state, runSetRef), runs)
      );
    }

    case getType(TempSelectionsActions.selectAll): {
      const {ref, runSetRef} = action.payload;

      return updateTempSelectionsWithUndo(state, ref, tempSelection =>
        selectAll(
          tempSelection,
          getRunSet(state, runSetRef),
          getGroupSelections(state, runSetRef)
        )
      );
    }
  }
  throw new Error('Action not undoable');
};

export const applyAndMakeInverseActionImmutable = (
  state: ViewReducerState,
  action: ActionType
): [ViewReducerState, ActionType] => {
  const [stateAfterApplyingAction, inverseAction] =
    applyAndMakeInverseActionImmutableInner(state, action);
  const stateAfterMarkingIsPanelsAuto = markSectionPanelsAutoImmutable(
    stateAfterApplyingAction,
    action,
    inverseAction
  );
  return [stateAfterMarkingIsPanelsAuto, inverseAction];
};

export const applyUndoableActionImmutable = (
  state: ViewReducerState,
  action: ActionType
) => {
  // Check if action should be undoable before computing inverse
  const payload = (action as any).payload;
  if (payload?.options?.isUndoable === false) {
    // Apply action but don't track undo/redo
    const [newState] = applyAndMakeInverseActionImmutable(state, action);
    return newState;
  }

  // Normal undoable path
  const [newState, inverseAction] = applyAndMakeInverseActionImmutable(
    state,
    action
  );

  if (newState === state) {
    // Reducer responded with a reference-identical value, so no state update
    // was made. Don't add to undo stack needlessly.
    return state;
  }

  // Reducer branch responded that undoing is a no-op, so don't add to undo
  // stack needlessly.
  if (inverseAction === NO_OP_UNDO) {
    return newState;
  }

  return Object.assign(newState, {
    undoActions: state.undoActions.concat({id: ID(), action: inverseAction}),
    redoActions: [],
  });
};
